import { debounce } from 'lodash-es';
import { useEffect, useRef, useState } from 'react';

type DebouncedFn<TArgs extends unknown[]> = ((...args: TArgs) => void) & {
  cancel: () => void;
};

export function useDebouncedCallback<TArgs extends unknown[]>(
  callback: (...args: TArgs) => void,
  delayMs = 300,
) {
  const callbackRef = useRef(callback);
  useEffect(() => {
    callbackRef.current = callback;
  });

  const cancelRef = useRef<() => void>(() => undefined);
  const invokeRef = useRef<(...args: TArgs) => void>(() => undefined);

  useEffect(() => {
    const fn = debounce((...args: TArgs) => {
      callbackRef.current(...args);
    }, delayMs);
    invokeRef.current = fn;
    cancelRef.current = () => fn.cancel();
    return () => fn.cancel();
  }, [delayMs]);

  const [stable] = useState<DebouncedFn<TArgs>>(() => {
    const fn = (...args: TArgs) => invokeRef.current(...args);
    fn.cancel = () => cancelRef.current();
    return fn;
  });

  return stable;
}
